import React, { useEffect, useState, useRef } from "react";
import { EthernetPort, Search, RotateCcw, Activity, Shield, Terminal, Globe, Zap, Hash, Clock, ChevronRight, Info, X, Link } from "lucide-react";
import baseapi from "../../../services/baseapi";

interface ProtocolEntry {
    id: number;
    protocol_type: string;
    raw_data: string;
    created: string;
    updated: string;
}

interface ServerProtocolsProps {
    hideHeader?: boolean;
}

const ServerProtocols: React.FC<ServerProtocolsProps> = ({
}) => {
    const [data, setData] = useState<ProtocolEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedEntry, setSelectedEntry] = useState<ProtocolEntry | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isProgrammaticScroll = useRef(false);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: asset_id, client_id: client_id }),
                limit: 100
            };

            const response = await baseapi.get("/asset_raw_protocols", { params });
            if (response.data.status) {
                const protocols = response.data.data || [];
                setData(protocols);
                if (protocols.length > 0 && !selectedEntry) {
                    setSelectedEntry(protocols[0]);
                }
            } else {
                setData([]);
            }
        } catch (error: any) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (asset_id) fetchData();
    }, [asset_id]);

    // Handle scroll to section when tab is clicked
    useEffect(() => {
        if (selectedEntry && isProgrammaticScroll.current) {
            const element = document.getElementById(`protocol-section-${selectedEntry.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            // Reset flag after animation duration approximately
            const timer = setTimeout(() => {
                isProgrammaticScroll.current = false;
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [selectedEntry]);

    // Scroll Spy Logic: Update active tab based on scroll position
    useEffect(() => {
        if (loading || data.length === 0) return;

        const observerOptions = {
            root: scrollContainerRef.current,
            threshold: [0, 0.1, 0.5, 1.0],
            rootMargin: "-20% 0px -70% 0px" // Trigger when section is in the top portion
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            if (isProgrammaticScroll.current) return;

            // Find the first entry that is intersecting
            const intersectingEntry = entries.find(e => e.isIntersecting);

            if (intersectingEntry) {
                const id = parseInt(intersectingEntry.target.id.replace('protocol-section-', ''));
                const matchingEntry = data.find(item => item.id === id);
                if (matchingEntry) {
                    setSelectedEntry(prev => prev?.id === matchingEntry.id ? prev : matchingEntry);
                }
            }
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        data.forEach(entry => {
            const el = document.getElementById(`protocol-section-${entry.id}`);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [data, loading]); // Removed selectedEntry from dependencies

    const getPortFromRaw = (rawData: any) => {
        try {
            const parsed =
                typeof rawData === "string" ? JSON.parse(rawData) : rawData;

            // Handle array of ports payload
            if (parsed.ports && Array.isArray(parsed.ports)) {
                return "Multiple";
            }

            // Handle payload where the root key is the port number (e.g. {"80": {...}})
            const keys = Object.keys(parsed);
            if (keys.length === 1 && !isNaN(Number(keys[0]))) {
                return keys[0];
            }

            return parsed.port ?? parsed.service_port ?? parsed.icmp_port ?? "0";
        } catch (e) {
            return "-";
        }
    };

    const getProtocolIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'ssh': return <Terminal className="w-4 h-4 text-emerald-400" />;
            case 'telnet': return <Terminal className="w-4 h-4 text-amber-400" />;
            case 'banner': return <Globe className="w-4 h-4 text-blue-400" />;
            case 'services': return <Shield className="w-4 h-4 text-purple-400" />;
            case 'arp': return <Activity className="w-4 h-4 text-cyan-400" />;
            case 'icmp': return <Zap className="w-4 h-4 text-orange-400" />;
            case 'ports': return <EthernetPort className="w-4 h-4 text-indigo-400" />;
            default: return <EthernetPort className="w-4 h-4 text-white" />;
        }
    };

    const filteredData = data.filter(entry =>
        entry.protocol_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getPortFromRaw(entry.raw_data).toString().includes(searchTerm)
    );

    const renderParsedData = (rawData: any, protocolType: string) => {
        try {
            let parsed = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
            const port = getPortFromRaw(rawData);
            const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
            const ip = info.ip_address || "Target Asset";

            // SPECIAL CASE: Render the exact `{"ports": [...]}` array as a grid table
            if (protocolType.toLowerCase() === 'ports' && parsed.ports && Array.isArray(parsed.ports)) {
                return (
                    <div className="space-y-6">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-800/60">
                            <Activity className="w-4 h-4 text-blue-400" />
                            <h4 className="text-xs font-black text-slate-300 uppercase tracking-widest">
                                {ip} - Discovered Ports
                            </h4>
                        </div>
                        <div className="rounded-xl border border-slate-800/40 overflow-hidden bg-slate-900/60">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-slate-900/80 border-b border-slate-800/60">
                                    <tr>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Port</th>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Protocol</th>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Service</th>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Status</th>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Product</th>
                                        <th className="px-4 py-3 text-[10px] font-black text-slate-200 uppercase tracking-widest">Version</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800/40">
                                    {parsed.ports.map((p: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-slate-800/30 transition-colors">
                                            <td className="px-4 py-3 text-[12px] font-black text-white">{p.port}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black uppercase ${p.protocol?.toLowerCase() === 'tcp'
                                                    ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                                    : 'bg-orange-500/10 text-orange-400 border border-orange-500/20'
                                                    }`}>
                                                    {p.protocol || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-[11px] font-bold text-blue-400">{p.service || '-'}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black ${p.status?.toLowerCase() === 'open' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                                                    <div className={`w-1 h-1 rounded-full ${p.status?.toLowerCase() === 'open' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                    {p.status ? p.status.toUpperCase() : 'UNKNOWN'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-[11px] font-bold text-slate-300">{p.product || '-'}</td>
                                            <td className="px-4 py-3 text-[11px] font-bold text-slate-300">{p.version || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            }

            // UNWRAP the data if it is keyed by the port number (e.g., {"80": { "banner": "..." }})
            // This prevents your other protocols from rendering as raw JSON blobs
            const rootKeys = Object.keys(parsed);
            if (rootKeys.length === 1 && !isNaN(Number(rootKeys[0])) && typeof parsed[rootKeys[0]] === 'object') {
                parsed = parsed[rootKeys[0]];
            }

            return (
                <div className="space-y-6">
                    {/* Service Group Header */}
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-800/60">
                        <Activity className="w-4 h-4 text-blue-400" />
                        <h4 className="text-xs font-black text-slate-300 uppercase tracking-widest">
                            {ip} - {port}/{protocolType}
                        </h4>
                    </div>

                    <div className="rounded-xl border border-slate-800/40 overflow-hidden bg-slate-900/60">
                        <table className="w-full border-collapse">
                            <tbody className="divide-y divide-slate-800/40">
                                {Object.entries(parsed).map(([key, value]) => {
                                    if (['id', 'asset_id', 'serial', 'firmware_version'].includes(key.toLowerCase())) return null;
                                    if (value === null || value === "" || value === undefined) return null;
                                    if (typeof value === 'object' && Object.keys(value).length === 0) return null;

                                    const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
                                    const isLongText = ['banner', 'http.body', 'response', 'error'].includes(key.toLowerCase()) || displayValue.length > 60 || typeof value === 'object';
                                    const formattedKey = key.split(/[._]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('.');

                                    if (isLongText) {
                                        return (
                                            <tr key={key} className="bg-slate-900/60 ">
                                                <td colSpan={2} className="p-4">
                                                    <div className="flex items-center gap-2 mb-3 text-orange-500/70">
                                                        <Hash className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] font-black uppercase tracking-widest leading-none">{formattedKey}</span>
                                                    </div>
                                                    <div className="bg-slate-950/80 rounded-lg p-4 border border-slate-800/80 font-mono text-[11px] leading-relaxed text-white whitespace-pre-wrap break-all shadow-inner">
                                                        {displayValue}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr key={key} className="group/row hover:bg-slate-800/20 transition-colors border-b border-slate-700/60">
                                            <td className="w-1/3 p-3 border-r border-slate-700/60">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="p-1 bg-slate-900/50 rounded-md border border-slate-800/50 group-hover/row:border-blue-500/30 transition-colors">
                                                        <Link className="w-3 h-3 text-blue-400" />
                                                    </div>
                                                    <span className="text-[10px] font-black text-white uppercase tracking-tighter group-hover/row:text-slate-300 transition-colors">
                                                        {formattedKey.toLowerCase()}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="p-1 bg-slate-900/50 rounded-md border border-slate-800/50 group-hover/row:border-emerald-500/30 transition-colors">
                                                        <Shield className="w-3 h-3 text-emerald-400" />
                                                    </div>
                                                    <div className="text-[11px] font-bold text-slate-200">
                                                        {typeof value === 'boolean' ? (
                                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black ${value ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                                                                <div className={`w-1 h-1 rounded-full ${value ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                                {value ? 'TRUE' : 'FALSE'}
                                                            </span>
                                                        ) : (key.toLowerCase().includes('status') && displayValue.toLowerCase() === 'up') ? (
                                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black bg-emerald-500/10 text-emerald-500">
                                                                <Activity className="w-2.5 h-2.5" />
                                                                UP
                                                            </span>
                                                        ) : displayValue}
                                                    </div>
                                                    {key.toLowerCase().includes('time') && <Clock className="w-3 h-3 text-slate-700 ml-auto" />}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        } catch (e) {
            return (
                <div className="p-8 bg-rose-500/5 border border-rose-500/10 rounded-2xl text-center">
                    <X className="w-8 h-8 text-rose-500/50 mx-auto mb-3" />
                    <p className="text-rose-500/70 text-xs font-bold uppercase tracking-widest">Protocol data parsing failed</p>
                </div>
            );
        }
    };

    return (
        <div className="flex flex-col h-full animate-fade-in bg-slate-900 border border-slate-800/40 overflow-hidden shadow-2xl">
            <div className="flex flex-1 overflow-hidden min-h-0">
                {/* Left Panel: Protocol List (Only show if data exists or loading) */}
                {(loading || data.length > 0) && (
                    <div className="w-1/5 lg:w-1/5 border-r border-slate-800/60 flex flex-col bg-slate-900/10">
                        <div className="p-3 border-b border-slate-800/40 bg-slate-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 bg-blue-600 rounded-md">
                                    <Info className="w-3 h-3 text-white" />
                                </div>
                                <span className="text-[11px] font-black text-white uppercase tracking-widest">Services</span>
                            </div>
                            <div className="flex items-center gap-2 px-1 mb-3 text-[10px] font-bold text-slate-500 tracking-tight">
                                <span>{info.ip_address || '-'}</span>
                            </div>
                            <div className="relative group">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600 w-3.5 h-3.5 group-focus-within:text-orange-500 transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    className="w-full bg-slate-950/60 border border-slate-800/80 rounded-lg pl-9 pr-2 py-2 text-[11px] text-white focus:outline-none focus:border-orange-500/50 transition-all font-bold placeholder:text-slate-700 shadow-inner"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scroll p-2 space-y-1.5">
                            {loading ? (
                                [...Array(12)].map((_, i) => (
                                    <div key={i} className="h-12 bg-slate-800/20 rounded-lg animate-pulse border border-slate-700/10"></div>
                                ))
                            ) : filteredData.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-slate-700 p-4 text-center opacity-40">
                                    <EthernetPort className="w-6 h-6 mb-2" />
                                    <p className="text-[9px] font-black uppercase tracking-widest leading-relaxed">No Port Found</p>
                                </div>
                            ) : (
                                filteredData.map((entry) => (
                                    <button
                                        key={entry.id}
                                        onClick={() => {
                                            isProgrammaticScroll.current = true;
                                            setSelectedEntry(entry);
                                        }}
                                        className={`w-full flex items-center gap-2.5 p-2 rounded-lg border transition-all duration-200 group text-left relative overflow-hidden active:scale-[0.98] ${selectedEntry?.id === entry.id
                                            ? "bg-orange-600/90 text-white border-orange-500 shadow-md"
                                            : "bg-slate-700/30 border-slate-800/50 text-white hover:border-slate-700 hover:bg-slate-800/40 shadow-sm"
                                            }`}
                                    >
                                        <div className={`p-1.5 rounded-md transition-colors ${selectedEntry?.id === entry.id ? "bg-white/20" : "bg-slate-950/50 group-hover:bg-slate-950"}`}>
                                            {getProtocolIcon(entry.protocol_type)}
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className={`text-[12px] font-black uppercase tracking-tight truncate ${selectedEntry?.id === entry.id ? "text-white" : "text-white"}`}>
                                                {entry.protocol_type?.replace(/_/g, " ")}
                                            </span>
                                        </div>
                                        {selectedEntry?.id === entry.id && (
                                            <ChevronRight className="absolute right-1.5 w-3 h-3 text-white/40" />
                                        )}
                                    </button>
                                ))
                            )}
                        </div>

                        <div className="p-2.5 border-t border-slate-800/40 bg-slate-900/20 flex items-center justify-between">
                            <span className="text-[8px] font-black text-slate-600 uppercase tracking-widest">{filteredData.length} Endpoints</span>
                            <button onClick={fetchData} className="p-1 hover:text-orange-500 transition-colors">
                                <RotateCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Right Panel: Detail View */}
                <div className={`flex flex-col overflow-hidden relative bg-slate-700/40 ${loading || data.length > 0 ? "flex-1 border-l border-slate-800/40" : "w-full"}`}>
                    <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 custom-scroll scroll-smooth">
                        <div className="space-y-12 pb-32">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center h-full opacity-20 mt-20">
                                    <RotateCcw className="w-8 h-8 animate-spin mb-2" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">Loading Protocol Data...</span>
                                </div>
                            ) : data.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-slate-700 p-8 text-center mt-20 rounded-xl">
                                    <Info className="w-12 h-12 opacity-30 mb-4 text-slate-500" />
                                    <h3 className="text-sm font-black uppercase tracking-widest mb-2 text-slate-400">The endpoint agent has not reported any protocol or service data for this asset yet.</h3>
                                    {/* <p className="text-[10px] font-medium text-slate-500 mt-2 max-w-xs">The endpoint agent has not reported any protocol or service data for this asset yet.</p> */}
                                </div>
                            ) : (
                                data.map((entry) => (
                                    <div
                                        key={entry.id}
                                        id={`protocol-section-${entry.id}`}
                                        className="transition-all duration-500 rounded-2xl p-1"
                                    >
                                        <div className="p-4 border-b border-slate-800/40 bg-slate-900/60 rounded-t-2xl flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg border transition-colors ${selectedEntry?.id === entry.id ? 'bg-orange-600 border-orange-500 shadow-lg' : 'bg-slate-800/80 border-slate-700/50'}`}>
                                                    <Info className={`w-3.5 h-3.5 ${selectedEntry?.id === entry.id ? 'text-white' : 'text-orange-500'}`} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <h3 className="text-sm font-black text-white uppercase tracking-tight flex items-center gap-2">
                                                        {entry.protocol_type.toLowerCase() === 'ports' ? 'Discovered Ports Overview' : `Port ${getPortFromRaw(entry.raw_data)} Details`}
                                                        <span className="text-[10px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded-full border border-emerald-500/20 font-black">ACTIVE</span>
                                                    </h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className="text-[10px] text-orange-500/80 font-black uppercase tracking-widest">{entry.protocol_type}</span>
                                                        <span className="text-[9px] text-white font-mono font-bold">UID: {entry.id}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="hidden sm:flex items-center gap-4 text-[10px] font-bold text-white uppercase tracking-tighter">
                                                <div className="flex items-center gap-1.5">
                                                    <Clock className="w-3 h-3" />
                                                    <span>Discovered {new Date(entry.created).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="px-1">
                                            {renderParsedData(entry.raw_data, entry.protocol_type)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerProtocols;